import { PageHero } from "./Properties";
import { Seo } from "@/components/site/Seo";
import { Reveal, MaskLine, Eyebrow } from "@/components/site/Reveal";
import { InquiryForm } from "@/components/site/InquiryForm";
import { useLang } from "@/lib/i18n";
import { useSite, useContent } from "@/lib/site";
import { WaButton } from "@/components/site/WhatsApp";

export function Partner() {
  const { t } = useLang();
  const { txt, img } = useContent();
  return (
    <main data-testid="partner-page">
      <Seo title={t.partner.title} path="/spolupraca" description={t.partner.teaser} />
      <PageHero img={img("partner_image", "/img/p2.jpg")} eyebrow={t.partner.eyebrow} title={txt("partnerTitle") || t.partner.title} />
      <section className="bg-ivory py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Reveal><p className="font-display text-2xl sm:text-3xl text-midnight leading-[1.5] max-w-4xl">{txt("partnerText") || t.partner.text}</p></Reveal>
          <div className="mt-20 grid md:grid-cols-3 gap-10">
            {t.partner.steps.map(([h, p], i) => (
              <Reveal key={h} delay={i * 0.12} className="border-t border-gold pt-8">
                <div className="font-display text-gold text-5xl">0{i + 1}</div>
                <h3 className="font-display text-3xl text-midnight mt-4">{h}</h3>
                <p className="text-midnight/75 mt-3 text-sm leading-relaxed">{p}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-20 grid lg:grid-cols-2 gap-14 items-start">
            <div className="bg-midnight p-10">
              <h2 className="font-display text-3xl text-ivory">{t.partner.teaser}</h2>
              <div className="mt-8"><WaButton testid="partner-wa" text={`${t.partner.title} – MEVALUX PROPERTY`} label={t.partner.cta} /></div>
            </div>
            <Reveal><InquiryForm /></Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}

export function About() {
  const { t } = useLang();
  const { txt, img } = useContent();
  return (
    <main data-testid="about-page">
      <Seo title={t.nav.about} path="/o-nas" />
      <PageHero img={img("about_image", "/img/int1.jpg")} eyebrow="MEVALUX PROPERTY" title={txt("aboutTitle")} />
      <section className="bg-ivory py-20 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <Reveal><p className="font-display text-2xl sm:text-3xl text-midnight leading-[1.5]">{txt("aboutText")}</p></Reveal>
          </div>
          <div className="lg:col-span-6 aspect-[4/5] overflow-hidden">
            <img src={img("about_image2", "/img/santorini2.jpg")} alt="" className="w-full h-full object-cover kenburns" />
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-24 grid md:grid-cols-3 gap-10">
          {t.values.map(([h, p], i) => (
            <Reveal key={h} delay={i * 0.12} className="border-t border-gold pt-8">
              <div className="font-display text-gold text-lg">0{i + 1}</div>
              <h3 className="font-display text-3xl text-midnight mt-3">{h}</h3>
              <p className="text-midnight/75 mt-3 text-sm leading-relaxed">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}

export function Contact() {
  const { t } = useLang();
  const { settings: s } = useSite();
  return (
    <main data-testid="contact-page">
      <Seo title={t.nav.contact} path="/kontakt" />
      <PageHero img="/img/sail.jpg" eyebrow={t.nav.contact} title={t.contactTitle} />
      <section className="bg-ivory py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="font-display text-4xl text-midnight"><MaskLine>{t.contactText}</MaskLine></h2>
            <div className="mt-12 space-y-6 text-midnight/85" data-testid="contact-info">
              <div><Eyebrow className="text-midnight/60 mb-2" line={false}>{s.company}</Eyebrow><p className="whitespace-pre-line">{s.address}</p></div>
              {s.phone && <a href={`tel:${s.phone}`} className="block font-display text-2xl hover:text-gold">{s.phone}</a>}
              {s.email && <a href={`mailto:${s.email}`} className="block font-display text-2xl hover:text-gold">{s.email}</a>}
            </div>
          </div>
          <Reveal><InquiryForm /></Reveal>
        </div>
      </section>
    </main>
  );
}

export function Legal({ kind }) {
  const { t, tr } = useLang();
  const { settings: s } = useSite();
  const title = { terms: t.footer.terms, gdpr: t.footer.gdpr, cookies: t.footer.cookies }[kind];
  return (
    <main data-testid={`legal-page-${kind}`} className="bg-ivory min-h-screen">
      <Seo title={title} />
      <section className="bg-midnight pt-40 pb-16">
        <div className="max-w-3xl mx-auto px-6"><h1 className="font-display text-4xl sm:text-5xl text-ivory"><MaskLine animateNow>{title}</MaskLine></h1></div>
      </section>
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="text-midnight/85 leading-[1.9] whitespace-pre-line" data-testid="legal-content">{tr(s.legal?.[kind])}</div>
      </section>
    </main>
  );
}
